import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, DiamondPlus, Grid, Layers, Menu, Palette, Sparkles, Trees, X, ShowerHead, Utensils, Sofa, Bed, StarIcon, CircleArrowOutDownRightIcon, HomeIcon, Info, Phone, LayoutGrid, Package, Building2, BadgePercent, BrickWall, MoveVertical } from "lucide-react";
import Logo3 from "./../../assets/logo3.png";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductOpen, setIsProductOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Menu items
  const menuItems = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  // Product items
  const productItems = [
    {
      name: "Floor",
      path: "/floor",
      description: "Glazed Vitrified Tiles",
      icon: Layers,
    },
    {
      name: "Porcelain Slab",
      path: "/porcelain-slab",
      description: "Polished Glazed Vitrified",
      icon: BadgePercent,
    },
    {
      name: "Wall",
      path: "/wall",
      description: "Matt Finish Tiles",
      icon: BrickWall,
    },
    // {
    //   name: "Parking",
    //   path: "/high-gloss",
    //   description: "High Gloss Ceramic",
    //   icon: DiamondPlus,
    // },
    {
      name: "Elevation",
      path: "/elevation",
      description: "Carved Design Tiles",
      icon: MoveVertical,
    },
    // {
    //   name: "Wooden",
    //   path: "/wooden",
    //   description: "Wood Look Tiles",
    //   icon: Trees,
    // },
  ];

  // Category items
  const categoryItems = [
    { name: "Bathroom", path: "/bathroom", icon: ShowerHead },
    { name: "Kitchen", path: "/kitchen", icon: Utensils },
    { name: "Leaving Area", path: "/leaving-area", icon: Sofa },
    { name: "Bedroom", path: "/bedroom", icon: Bed },
    { name: "Commercial Space", path: "/commercial-space", icon: Bed },
    { name: "Outdoor", path: "/outdoor", icon: CircleArrowOutDownRightIcon },
    { name: "Counter Tops", path: "/counter-tops", icon: Bed },
    { name: "Stairs Tils", path: "/stairs-tils", icon: StarIcon },
  ];

  // Check if category or product is active
  const isCategoryActive = () => categoryItems.some((item) => location.pathname === item.path);
  const isProductActive = () => productItems.some((item) => location.pathname === item.path);
  const isActivePath = (path) => location.pathname === path;

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsProductOpen(false);
    setIsCategoryOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "bg-white shadow-lg backdrop-blur-md" : "bg-white shadow-md"
        }`}
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo Section */}
          <div className="flex-shrink-0">
            <Link to="/">
              <img
                src={Logo3}
                alt="GMC Ceramic Logo"
                className="h-10 sm:h-14 lg:h-14 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center">
            {/* Home */}
            <Link
              to="/"
              className={`relative px-3 py-2 text-sm lg:text-base font-semibold transition-all duration-300 rounded-lg group ml-4 ${isActivePath("/") ? "text-orange-600 bg-orange-50" : "text-gray-700 hover:text-orange-600 hover:bg-orange-50"
                }`}
            >
              Home
              <span
                className={`absolute bottom-0 left-0 w-full h-0.5 bg-orange-600 transform origin-left transition-transform duration-300 ${isActivePath("/") ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                  }`}
              ></span>
            </Link>

            {/* About */}
            <Link
              to="/about"
              className={`relative px-3 py-2 text-sm lg:text-base font-semibold transition-all duration-300 rounded-lg group ml-4 ${isActivePath("/about") ? "text-orange-600 bg-orange-50" : "text-gray-700 hover:text-orange-600 hover:bg-orange-50"
                }`}
            >
              About
              <span
                className={`absolute bottom-0 left-0 w-full h-0.5 bg-orange-600 transform origin-left transition-transform duration-300 ${isActivePath("/about") ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                  }`}
              ></span>
            </Link>

            {/* Products Mega Menu */}
            <div className="relative group ml-4">
              <button
                className={`flex items-center space-x-1 px-3 py-2 text-sm lg:text-base font-semibold transition-all duration-300 rounded-lg ${isProductActive() || isProductOpen ? "text-orange-600 bg-orange-50" : "text-gray-700 hover:text-orange-600 hover:bg-orange-50"
                  }`}
                onClick={() => setIsProductOpen(!isProductOpen)}
                onMouseEnter={() => setIsProductOpen(true)}
                aria-expanded={isProductOpen}
                aria-controls="products-menu"
              >
                <span>Products</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isProductOpen ? "rotate-180" : ""}`} />
                <span
                  className={`absolute bottom-0 left-0 w-full h-0.5 bg-orange-600 transform origin-left transition-transform duration-300 ${isProductActive() || isProductOpen ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                    }`}
                ></span>
              </button>
              {/* Mega Menu Dropdown */}
              <div
                id="products-menu"
                className={`absolute top-full right-0 mr-4 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-100 transition-all duration-300 transform ${isProductOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-4"
                  }`}
                onMouseLeave={() => setIsProductOpen(false)}
              >
                <div className="p-4 sm:p-6">
                  <div className="mb-4">
                    <h3 className="text-base sm:text-xl font-bold text-gray-800 mb-1">Our Product Range</h3>
                    <p className="text-xs sm:text-sm text-gray-500">Premium ceramic tiles for every space</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {productItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`p-3 rounded-lg transition-all duration-300 group border ${isActivePath(item.path) ? "bg-orange-50 border-orange-200 text-orange-600" : "hover:bg-orange-50 hover:border-orange-200 border-gray-100"
                          }`}
                        onClick={() => setIsProductOpen(false)}
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-xl sm:text-2xl">
                            <item.icon />
                          </span>
                          <div>
                            <h4
                              className={`font-semibold text-sm sm:text-base ${isActivePath(item.path) ? "text-orange-600" : "text-gray-800 group-hover:text-orange-600"
                                }`}
                            >
                              {item.name}
                            </h4>
                            <p className="text-xs text-gray-500">{item.description}</p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Category Mega Menu */}
            <div className="relative group ml-4">
              <button
                className={`flex items-center space-x-1 px-3 py-2 text-sm lg:text-base font-semibold transition-all duration-300 rounded-lg ${isCategoryActive() || isCategoryOpen ? "text-orange-600 bg-orange-50" : "text-gray-700 hover:text-orange-600 hover:bg-orange-50"
                  }`}
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                onMouseEnter={() => setIsCategoryOpen(true)}
                aria-expanded={isCategoryOpen}
                aria-controls="category-menu"
              >
                <span>Category</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isCategoryOpen ? "rotate-180" : ""}`} />
                <span
                  className={`absolute bottom-0 left-0 w-full h-0.5 bg-orange-600 transform origin-left transition-transform duration-300 ${isCategoryActive() || isCategoryOpen ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                    }`}
                ></span>
              </button>
              {/* Category Dropdown */}
              <div
                id="category-menu"
                className={`absolute top-full right-0 mr-4 mt-2 mb-6 w-64 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-100 transition-all duration-300 transform ${isCategoryOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-4"
                  }`}
                onMouseLeave={() => setIsCategoryOpen(false)}
              >
                <div className="p-4 sm:p-6">
                  <div className="mb-4">
                    <h3 className="text-base sm:text-xl font-bold text-gray-800 mb-1">Tile Categories</h3>
                    <p className="text-xs sm:text-sm text-gray-500">Browse by space</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {categoryItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`p-3 rounded-lg transition-all duration-300 group border mb-2 ${isActivePath(item.path) ? "bg-orange-50 border-orange-200 text-orange-600" : "hover:bg-orange-50 hover:border-orange-200 border-gray-100"
                          }`}
                        onClick={() => setIsCategoryOpen(false)}
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-xl sm:text-2xl">
                            <item.icon />
                          </span>
                          <div>
                            <h4
                              className={`font-semibold text-sm sm:text-base ${isActivePath(item.path) ? "text-orange-600" : "text-gray-800 group-hover:text-orange-600"
                                }`}
                            >
                              {item.name}
                            </h4>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Contact */}
            <Link
              to="/contact"
              className={`relative px-3 py-2 text-sm lg:text-base font-semibold transition-all duration-300 rounded-lg group ml-4 ${isActivePath("/contact") ? "text-orange-600 bg-orange-50" : "text-gray-700 hover:text-orange-600 hover:bg-orange-50"
                }`}
            >
              Contact
              <span
                className={`absolute bottom-0 left-0 w-full h-0.5 bg-orange-600 transform origin-left transition-transform duration-300 ${isActivePath("/contact") ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                  }`}
              ></span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors duration-300"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-0 top-16 sm:top-20 z-[100] transition-opacity duration-300 ${isMenuOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
          }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50 z-[90] transition-opacity duration-300"
          onClick={() => setIsMenuOpen(false)}
        />

        {/* Menu Container */}
        <div
          className={`relative bg-orange-100 to-white w-[80%] sm:w-[70%] max-w-[400px] h-[calc(100vh-4rem)] sm:h-[calc(100vh-5rem)] overflow-y-auto transition-transform duration-300 z-[100] ${isMenuOpen ? "translate-x-0" : "-translate-x-full"
            }`}
        >
          <div className="px-4 py-6 space-y-3 mobile-menu-container">
            {/* Home */}
            <Link
              to="/"
              className={`flex items-center py-3 px-4 text-base font-semibold rounded-xl transition-all duration-300 transform hover:scale-[1.02] ${isActivePath("/")
                ? "text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-lg shadow-orange-200"
                : "text-gray-700 bg-white/80 backdrop-blur-sm hover:bg-orange-50 border border-gray-100"
                }`}
              onClick={() => setIsMenuOpen(false)}
            >
              <span className="mr-3 text-xl flex items-center">
                <HomeIcon className="w-5 h-5" />
              </span>
              Home
              {isActivePath("/") && <span className="ml-auto">→</span>}
            </Link>

            {/* About */}
            <Link
              to="/about"
              className={`flex items-center py-3 px-4 text-base font-semibold rounded-xl transition-all duration-300 transform hover:scale-[1.02] ${isActivePath("/about")
                ? "text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-lg shadow-orange-200"
                : "text-gray-700 bg-white/80 backdrop-blur-sm hover:bg-orange-50 border border-gray-100"
                }`}
              onClick={() => setIsMenuOpen(false)}
            >
              <span className="mr-3 text-xl">
                <Info className="w-5 h-5" />
              </span>
              About
              {isActivePath("/about") && <span className="ml-auto">→</span>}
            </Link>

            {/* Products Dropdown */}
            <div className="space-y-2">
              <button
                className={`flex items-center justify-between w-full py-3 px-4 text-base font-semibold rounded-xl transition-all duration-300 transform hover:scale-[1.02] ${isProductActive() || isProductOpen
                  ? "text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-lg shadow-orange-200"
                  : "text-gray-700 bg-white/80 backdrop-blur-sm hover:bg-orange-50 border border-gray-100"
                  }`}
                onClick={() => setIsProductOpen(!isProductOpen)}
                aria-expanded={isProductOpen}
                aria-controls="mobile-products-menu"
              >
                <div className="flex items-center">
                  <span className="mr-3 text-xl">
                    <Package className="w-5 h-5" />
                  </span>
                  <span>Products</span>
                </div>
                <ChevronDown
                  className={`w-5 h-5 transition-transform duration-300 ${isProductOpen ? "rotate-180" : ""}`}
                />
              </button>
              <div
                id="mobile-products-menu"
                className={`overflow-hidden transition-all duration-300 ${isProductOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                  }`}
              >
                <div className="pl-2 space-y-2 mt-1">
                  {productItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center space-x-3 py-3 px-4 text-sm rounded-xl transition-all duration-300 transform hover:scale-[1.02] ${isActivePath(item.path)
                        ? "text-white bg-gradient-to-r from-orange-400 to-orange-500 shadow-md shadow-orange-200"
                        : "text-gray-700 bg-white/80 backdrop-blur-sm hover:bg-orange-50 border border-gray-100"
                        }`}
                      onClick={() => {
                        setIsProductOpen(false);
                        setIsMenuOpen(false);
                      }}
                    >
                      <span className={`text-lg flex-shrink-0 ${isActivePath(item.path) ? "text-white" : "text-orange-500"}`}>
                        <item.icon />
                      </span>
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold block">{item.name}</span>
                        <p className={`text-xs ${isActivePath(item.path) ? "text-orange-100" : "text-gray-500"}`}>{item.description}</p>
                      </div>
                      {isActivePath(item.path) && <span className="ml-auto">✓</span>}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Category Dropdown */}
            <div className="space-y-2">
              <button
                className={`flex items-center justify-between w-full py-3 px-4 text-base font-semibold rounded-xl transition-all duration-300 transform hover:scale-[1.02] ${isCategoryActive() || isCategoryOpen
                  ? "text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-lg shadow-orange-200"
                  : "text-gray-700 bg-white/80 backdrop-blur-sm hover:bg-orange-50 border border-gray-100"
                  }`}
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                aria-expanded={isCategoryOpen}
                aria-controls="mobile-category-menu"
              >
                <div className="flex items-center">
                  <span className="mr-3 text-xl">
                    <LayoutGrid className="w-5 h-5" />
                  </span>
                  <span>Category</span>
                </div>
                <ChevronDown
                  className={`w-5 h-5 transition-transform duration-300 ${isCategoryOpen ? "rotate-180" : ""}`}
                />
              </button>
              <div
                id="mobile-category-menu"
                className={`overflow-hidden transition-all duration-300 ${isCategoryOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                  }`}
              >
                <div className="pl-2 space-y-2 mt-1">
                  {categoryItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center space-x-3 py-3 px-4 text-sm rounded-xl transition-all duration-300 transform hover:scale-[1.02] ${isActivePath(item.path)
                        ? "text-white bg-gradient-to-r from-orange-400 to-orange-500 shadow-md shadow-orange-200"
                        : "text-gray-700 bg-white/80 backdrop-blur-sm hover:bg-orange-50 border border-gray-100"
                        }`}
                      onClick={() => {
                        setIsCategoryOpen(false);
                        setIsMenuOpen(false);
                      }}
                    >
                      <span className={`text-lg flex-shrink-0 ${isActivePath(item.path) ? "text-white" : "text-orange-500"}`}>
                        <item.icon />
                      </span>
                      <span className="font-semibold flex-1">{item.name}</span>
                      {isActivePath(item.path) && <span className="ml-auto">✓</span>}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact */}
            <Link
              to="/contact"
              className={`flex items-center py-3 px-4 text-base font-semibold rounded-xl transition-all duration-300 transform hover:scale-[1.02] ${isActivePath("/contact")
                ? "text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-lg shadow-orange-200"
                : "text-gray-700 bg-white/80 backdrop-blur-sm hover:bg-orange-50 border border-gray-100"
                }`}
              onClick={() => setIsMenuOpen(false)}
            >
              <span className="mr-3 text-xl">
                <Phone className="w-5 h-5" />
              </span>
              Contact
              {isActivePath("/contact") && <span className="ml-auto">→</span>}
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;